import { useEffect, useState, useCallback } from "react";
import api, { errMsg } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader, Card, Field, Btn, Badge, Modal, inputCls } from "../components/common";
import { fmtDate } from "../lib/format";
import { toast } from "sonner";
import { Plus, CalendarOff, Check, X } from "lucide-react";

const TONE = { pending: "amber", disetujui: "emerald", ditolak: "rose" };
const LABEL = { pending: "Menunggu Approval", disetujui: "Disetujui", ditolak: "Ditolak" };

export default function Cuti() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [leaveDate, setLeaveDate] = useState("");
  const [reason, setReason] = useState("");

  const canApprove = ["Super Admin", "Direktur", "Global Admin"].includes(user?.role);
  const load = useCallback(() => { api.get("/leave-requests").then((r) => setRows(r.data)).catch((e) => toast.error(errMsg(e))); }, []);
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (!leaveDate || !reason.trim()) return toast.error("Tanggal cuti dan alasan wajib diisi");
    try {
      await api.post("/leave-requests", { leave_date: leaveDate, reason });
      toast.success("Pengajuan cuti terkirim — menunggu approval Direktur / Global Admin");
      setShowForm(false); setLeaveDate(""); setReason(""); load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const act = async (id, action) => {
    try {
      await api.post(`/leave-requests/${id}/action`, { action });
      toast.success(action === "approve" ? "Cuti disetujui" : "Cuti ditolak");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const mine = rows.filter((r) => r.user_id === user?.id);
  const toApprove = canApprove ? rows.filter((r) => r.status === "pending" && r.user_id !== user?.id) : [];
  const history = canApprove ? rows.filter((r) => r.status !== "pending") : mine;

  return (
    <div data-testid="cuti-page">
      <PageHeader title="Pengajuan Cuti" subtitle="Pengajuan cuti terpisah dari absensi — wajib diajukan minimal H-3, di-approve Direktur / Global Admin" testid="cuti-header">
        <Btn onClick={() => setShowForm(true)} data-testid="cuti-add-button"><Plus className="w-4 h-4" /> Ajukan Cuti</Btn>
      </PageHeader>

      {canApprove && toApprove.length > 0 && (
        <div className="mb-6" data-testid="cuti-approval-section">
          <div className="text-xs font-mono uppercase tracking-wider text-amber-400 mb-2">Menunggu Approval Anda ({toApprove.length})</div>
          <div className="space-y-2">
            {toApprove.map((r) => (
              <Card key={r.id} className="!p-4 !border-amber-500/50" testid={`cuti-approve-${r.id}`}>
                <div className="flex flex-wrap items-center gap-2">
                  <CalendarOff className="w-4 h-4 text-amber-400" />
                  <span className="font-medium text-slate-200">{r.user_name}</span>
                  <span className="text-xs text-slate-500">{r.user_role}</span>
                  <span className="font-mono text-xs text-emerald-400">{fmtDate(r.leave_date)}</span>
                </div>
                <p className="text-xs text-slate-400 mt-1.5">{r.reason}</p>
                <div className="flex gap-2 mt-3">
                  <Btn className="!text-xs !py-1.5" onClick={() => act(r.id, "approve")} data-testid={`cuti-approve-btn-${r.id}`}><Check className="w-3.5 h-3.5" /> Setujui</Btn>
                  <Btn tone="danger" className="!text-xs !py-1.5" onClick={() => act(r.id, "reject")} data-testid={`cuti-reject-btn-${r.id}`}><X className="w-3.5 h-3.5" /> Tolak</Btn>
                </div>
              </Card>
            ))}
          </div>
        </div>
      )}

      <div className="text-xs font-mono uppercase tracking-wider text-slate-400 mb-2">{canApprove ? "Riwayat Semua Pengajuan" : "Pengajuan Saya"}</div>
      <div className="space-y-2" data-testid="cuti-list">
        {history.length === 0 && <Card><p className="text-slate-500 text-sm">Belum ada pengajuan cuti.</p></Card>}
        {history.map((r) => (
          <Card key={r.id} className="!p-4" testid={`cuti-item-${r.id}`}>
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-medium text-slate-200 flex-1 min-w-[140px]">{r.user_name}</span>
              <Badge tone={TONE[r.status] || "slate"} data-testid={`cuti-status-${r.id}`}>{LABEL[r.status] || r.status}</Badge>
              <span className="font-mono text-xs text-slate-400">{fmtDate(r.leave_date)}</span>
            </div>
            <p className="text-xs text-slate-500 mt-1">{r.reason}{r.action_by ? ` — diproses oleh ${r.action_by}` : ""}</p>
          </Card>
        ))}
      </div>

      <Modal open={showForm} onClose={() => setShowForm(false)} title="Pengajuan Cuti Baru" testid="cuti-form-modal">
        <div className="space-y-3">
          <Field label="Tanggal Mulai Cuti (wajib, minimal H-3)">
            <input type="date" className={inputCls} min={new Date(Date.now() + 3 * 86400000).toISOString().slice(0, 10)} value={leaveDate} onChange={(e) => setLeaveDate(e.target.value)} data-testid="cuti-date-input" />
          </Field>
          <Field label="Alasan Cuti (wajib)">
            <textarea className={inputCls} rows={3} placeholder="Contoh: cuti tahunan / keperluan keluarga" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="cuti-reason-input" />
          </Field>
          <div className="flex justify-end gap-2">
            <Btn tone="ghost" onClick={() => setShowForm(false)}>Batal</Btn>
            <Btn onClick={submit} data-testid="cuti-submit-button">Kirim Pengajuan</Btn>
          </div>
        </div>
      </Modal>
    </div>
  );
}
